import { useMemo, useRef, useState } from 'react';
import { FloatingPanelProvider, FloatingPanel } from '@slope/r3f';
import {
  GetLeaderBoardQuery,
  Hole,
  ParticipationStatus,
  useGetLeaderBoardQuery,
} from '@slope/graphql/apollo';
import { isHole, toVec3 } from '@slope/three';
import { useFrame, useThree } from '@react-three/fiber';
import type * as R3F from '@react-three/fiber';
import * as THREE from 'three';
import {
  HolePlayerInfoPanel,
  HolePlayerInfoPanelProps,
  HolePlayerInfoPanelVariant,
  HolePlayerInfoRow,
  ThemeProvider,
} from '@slope/uikit';
import { a, config, useSpring } from '@react-spring/three';
import { compareScores } from '@slope/utils';
import { useStore } from '@slope/store';

const v1 = /*__@PURE__*/ new THREE.Vector3();
const AnimatedFloatingPanel = a(FloatingPanel);

const getHoleFromScene = (scene: THREE.Scene, holeNumber: number) => {
  const obj = scene.getObjectByName(`hole_${holeNumber}`);
  if (!obj || !isHole(obj)) return null;
  return obj;
};

const getHitPosition = (object: THREE.Object3D, camera: THREE.Camera) => {
  const bbox = new THREE.Box3().setFromObject(object);
  const position = bbox.getCenter(new THREE.Vector3());
  const ray = new THREE.Raycaster();
  ray.set(camera.position, toVec3(position).sub(camera.position).normalize());
  const intersects = ray.intersectObject(object, true);
  const hit = intersects.length > 0;

  return { position, hit };
};

type PartialHole = Pick<Hole, 'id' | 'number'>;
type LeaderBoardRankingFromQuery = NonNullable<GetLeaderBoardQuery['leaderBoard']>['ranking'][0];

/* ---------- Hole panel ---------- */

export type HolePlayerFloatingPanelProps = Omit<
  HolePlayerInfoPanelProps,
  'variant' | 'position' | 'holeNumber'
> & {
  id: string;
  holeNumber: number;
  scores: LeaderBoardRankingFromQuery[];
  expanded?: boolean;
  position: R3F.Vector3;
};

const HolePlayerFloatingPanel: React.FC<HolePlayerFloatingPanelProps> = ({
  id,
  holeNumber,
  scores,
  position,
  expanded = false,
  ...restProps
}) => {
  const camera = useThree(({ camera }) => camera);
  const [variant, setVariant] = useState<HolePlayerInfoPanelVariant>(
    HolePlayerInfoPanelVariant.Detailed,
  );

  const springs = useSpring({
    position: useMemo(() => toVec3(position, v1).toArray(), [position]),
    config: config.slow,
  });

  const lastVariant = useRef(variant);

  useFrame(() => {
    const distance = camera.position.distanceTo(toVec3(position, v1));
    const logDist = Math.log(distance + 1);
    let next: HolePlayerInfoPanelVariant;
    if (logDist < Math.log(600)) next = HolePlayerInfoPanelVariant.Simplified;
    else next = HolePlayerInfoPanelVariant.Compact;

    if (lastVariant.current !== next) {
      lastVariant.current = next;
      setVariant(next);
    }
  });

  const actualVariant = expanded ? HolePlayerInfoPanelVariant.Detailed : variant;

  const nodes = useMemo(() => {
    return scores.reduce((acc, { score, participation }) => {
      return score
        ? [
            ...acc,
            <HolePlayerInfoRow
              key={participation.id}
              variant={actualVariant}
              firstName={participation.players[0]?.firstName ?? ''}
              lastName={participation.players[0]?.lastName ?? ''}
              score={score.toPar ?? 0}
            />,
          ]
        : acc;
    }, [] as React.ReactNode[]);
  }, [scores, actualVariant]);

  return (
    <AnimatedFloatingPanel
      id={`panel_${id}`}
      position={springs.position}
      distanceFactor={400}
      offset={[0, -100]}
      margin={10}
      markerColor={'#454545'}
    >
      <ThemeProvider>
        <HolePlayerInfoPanel
          variant={actualVariant}
          holeNumber={holeNumber}
          teeTime="+10:00"
          startHole={1}
          startTime="09:00"
          {...restProps}
        >
          {nodes}
        </HolePlayerInfoPanel>
      </ThemeProvider>
    </AnimatedFloatingPanel>
  );
};

/* ---------- Panels container ---------- */

export type PanelsProps = { tournamentId: string };

export const Panels: React.FC<PanelsProps> = ({ tournamentId }) => {
  const scene = useThree(({ scene }) => scene);
  const lastClosestHole = useRef<Hole | null>(null);
  const lastUpdateTime = useRef<number>(0);
  const [closestHole, setClosestHole] = useState<Hole | null>(null);
  const camera = useThree(({ camera }) => camera);
  const higlight = useStore(({ highlight }) => highlight);

  const { data } = useGetLeaderBoardQuery({
    skip: !tournamentId,
    variables: { tournamentId },
  });

  const scores = useMemo(() => data?.leaderBoard.ranking ?? [], [data?.leaderBoard]);

  const validScores = useMemo(() => scores.filter((s) => s.score !== null), [scores]);

  const filteredScores = useMemo<LeaderBoardRankingFromQuery[]>(() => {
    if (!higlight?.hole?.number) return validScores;
    const num = higlight.hole.number;
    return validScores.filter((s) => {
      const lh = s.participation.lastScoredHole?.number;
      return lh === num;
    });
  }, [higlight, validScores]);

  const groupedByHole = useMemo(() => {
    const map = new Map<number, { id: string | null; entries: LeaderBoardRankingFromQuery[] }>();

    for (const entry of filteredScores) {
      const holeNum =
        entry.participation.lastScoredHole?.number ?? entry.participation.startHole?.number;
      if (!holeNum) continue;

      const holeId =
        entry.participation.lastScoredHole?.id ?? entry.participation.startHole?.id ?? null;

      const bucket = map.get(holeNum);
      if (bucket) {
        bucket.entries.push(entry);
      } else {
        map.set(holeNum, { id: holeId, entries: [entry] });
      }
    }

    return map;
  }, [filteredScores]);

  // Build floating panel payloads per visible hole
  const holeScores = useMemo(
    () =>
      Array.from(groupedByHole.entries()).reduce(
        (acc, [holeNumber, { id, entries }]) => {
          const holeObject = getHoleFromScene(scene, holeNumber);
          if (!holeObject) return acc;
          const { position, hit } = getHitPosition(holeObject, camera);
          if (!hit) return acc;

          const top = entries
            .map((entry) => ({
              view: entry, // keep original entry for rendering
              cmp: {
                status: entry.participation.status as ParticipationStatus,
                score: entry.score?.toPar ?? null,
                teeTime: entry.participation.teeTime ?? null,
              },
            }))
            .sort((a, b) => compareScores(a.cmp, b.cmp))
            .slice(0, 4)
            .map((x) => x.view);

          return [
            ...acc,
            {
              hole: { id: id ?? `hole-${holeNumber}`, number: holeNumber },
              position: toVec3(position).toArray() as THREE.Vector3Tuple,
              scores: top as LeaderBoardRankingFromQuery[],
            },
          ];
        },
        [] as {
          hole: PartialHole;
          position: THREE.Vector3Tuple;
          scores: LeaderBoardRankingFromQuery[];
        }[],
      ),
    [camera, groupedByHole, scene],
  );

  // Find the hole with the “best” (lowest) toPar among its top entries
  const { hole: highestScoreHole } = useMemo(() => {
    return holeScores.reduce(
      (acc, { hole, scores }) => {
        const bestOnHole = scores.reduce<number>(
          (best, p) => (p.score ? Math.min(best, p.score.toPar) : best),
          Infinity,
        );
        if (bestOnHole < acc.score) return { hole, score: bestOnHole };
        return acc;
      },
      { hole: null as PartialHole | null, score: Infinity },
    );
  }, [holeScores]);

  // Track nearest hole to camera to toggle "expanded"
  useFrame(() => {
    let nearest: PartialHole | null = null;
    let nearestDist = Infinity;

    holeScores.forEach(({ hole, position }) => {
      const dist = camera.position.distanceToSquared(toVec3(position));
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = hole;
      }
    });

    if (nearest) {
      if (lastClosestHole.current !== nearest && lastUpdateTime.current + 600 < performance.now()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        lastClosestHole.current = nearest as any;
        lastUpdateTime.current = performance.now();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setClosestHole(nearest as any);
      }
    } else if (lastClosestHole.current !== null) {
      lastClosestHole.current = null;
      setClosestHole(null);
    }
  });

  const panels = useMemo(() => {
    return holeScores.map(({ hole, position, scores }) => (
      <HolePlayerFloatingPanel
        key={hole.id}
        id={hole.id}
        holeNumber={hole.number} // ⬅️ NEW
        scores={scores}
        position={position}
        expanded={closestHole === hole || highestScoreHole === hole}
      />
    ));
  }, [closestHole, highestScoreHole, holeScores]);

  return <FloatingPanelProvider>{panels}</FloatingPanelProvider>;
};
